import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { ChevronDown, Waves, Dumbbell, UtensilsCrossed, Sparkles } from 'lucide-react';

const faqs = [
  {
    q: "Does Rudragarh have a swimming pool?",
    a: "Yes — Rudragarh has a swimming pool on the property, available to all guests. Set against the desert landscape, the pool is a genuine retreat from the Rajasthan heat. It's one of the few resorts near Sam Sand Dunes with a proper pool rather than just a splash zone."
  },
  {
    q: "Is the pool open year-round at Rudragarh?",
    a: "The pool is available to guests throughout their stay. Jaisalmer has extreme temperatures in summer (April–June can reach 45°C), making the pool especially valuable then. During winter months (November–February), when desert nights are cold, daytime temperatures are mild enough for pool use in the afternoon sun."
  },
  {
    q: "What other facilities are available at Rudragarh besides the pool?",
    a: "Rudragarh has a fully equipped gym, grand dining hall, bar room, traditional low-sitting hall, live and open kitchen, private sand dunes, natural pond and waterfall, horse riding, ATV rides, camel safari, football field, archery range, air gun range, rain dance floor, kids' play zone, cycling and jogging tracks, cultural programs, open-air theatre, greenhouse, and photo booths across the property. All facilities are included with every booking."
  }
];

const facilities = [
  { icon: <Waves size={28} />, title: "Swimming Pool", desc: "Cool off in the pool after a morning dune ride. A genuine desert oasis on-site." },
  { icon: <Dumbbell size={28} />, title: "Fully Equipped Gym", desc: "Keep your routine going — or start one. The gym is well-stocked and open to all guests." },
  { icon: <UtensilsCrossed size={28} />, title: "Grand Dining & Bar", desc: "A grand dining hall, traditional low-sitting hall, live kitchen, and bar — all on-property." },
  { icon: <Sparkles size={28} />, title: "Desert Experiences", desc: "ATV rides, horse riding, camel safari, cultural evenings, open-air theatre — included." }
];

const rooms = [
  { slug: "desert-riad-suite", name: "The Desert Riad Suite", price: 18000, guests: 6, category: "modern", desc: "A spacious riad-inspired flat suite with a full kitchen and premium comforts — room for 4–6, with the pool a short walk away." },
  { slug: "grand-quad", name: "The Grand Quad", price: 7000, guests: 4, category: "modern", desc: "The resort's grandest room — a king bed steps from the bar, pool, and garden." },
  { slug: "cascade-cottages", name: "Cascade Cottages", price: 12000, guests: 2, category: "heritage", desc: "A private water-facing cottage with a traditional Jharoka window seat and cascade pond & waterfall views — the pool is steps away." }
];

const ResortWithPool = () => {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <div className="min-h-screen pt-20">
      <Helmet>
        <title>Resort with Swimming Pool in Jaisalmer | Rudragarh</title>
        <meta name="description" content="Beat the desert heat at Rudragarh — a Jaisalmer resort with a swimming pool, heritage stays, cave rooms, and desert activities near Sam Sand Dunes." />
        <meta property="og:title" content="Resort with Swimming Pool in Jaisalmer | Rudragarh" />
        <meta property="og:description" content="Rudragarh is a boutique Jaisalmer resort with a pool, gym, bar, cave stays, heritage cottages, and desert experiences near Sam Sand Dunes." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.rudragarh.com/jaisalmer/resort-with-pool" />
        <meta property="og:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Jaisalmer Resort with Pool | Rudragarh" />
        <meta name="twitter:description" content="Beat the desert heat at Rudragarh — pool, gym, bar, cave stays, and 15+ desert activities near Sam Sand Dunes." />
        <meta name="twitter:image" content="https://www.rudragarh.com/images/resort-pool-night.jpg" />
        <link rel="canonical" href="https://www.rudragarh.com/jaisalmer/resort-with-pool" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(f => ({
            "@type": "Question",
            "name": f.q,
            "acceptedAnswer": { "@type": "Answer", "text": f.a }
          }))
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] flex items-center justify-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pool-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/50 to-void" />
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <span className="overline">Jaisalmer Resort</span>
            <h1 className="text-4xl md:text-6xl font-heading font-medium mb-4">
              A Desert <span className="text-gold-500">Oasis</span> with Pool
            </h1>
            <p className="text-foreground/70 max-w-2xl mx-auto text-lg">
              The Thar's heat is real — so is the relief. Rudragarh's pool sits in the heart of a resort with 15+ on-site facilities, all included.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <span className="overline">The Facilities</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              More than a pool — a <span className="text-gold-500">complete resort</span>
            </h2>
            <p className="text-foreground/70 leading-relaxed">
              Jaisalmer sees summer temperatures above 45°C. Finding a resort with a proper pool near Sam Sand Dunes is harder than it sounds — most desert camps are exactly that: camps, with canvas roofs and bucket showers. Rudragarh is built differently. A real pool. A real gym. A real bar.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              The pool fits into a broader picture of facilities that puts Rudragarh in a different category from tent-based desert stays. After a morning ATV ride on the dunes, you return to the pool. After lunch in the grand dining hall, you nap in your heritage cottage. After sundown, the open-air theatre and cultural programs take over. The balance of adventure and comfort is the point.
            </p>
            <p className="text-foreground/70 leading-relaxed">
              And there's also a natural pond and waterfall on the property — a feature unique to Rudragarh in this stretch of Rajasthan.
            </p>
          </div>
        </div>
      </section>

      {/* Full Facility List */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Included With Every Booking</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              18 Facilities, <span className="text-gold-500">All Included</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              "Swimming Pool", "Fully Equipped Gym", "Grand Dining Hall", "Traditional Low-Sitting Hall",
              "Live/Open Kitchen", "Bar Room", "Private Sand Dunes", "Natural Pond & Waterfall",
              "Horse Riding", "ATV on the Dunes", "Camel Safari", "Football & Archery",
              "Air Gun Range", "Rain Dance Floor", "Kids' Play Zone", "Cycling & Jogging Tracks",
              "Cultural Programs", "Open-Air Theatre"
            ].map((facility, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.03 }}
                className="bg-void-subtle border border-gold-500/10 p-4 text-center"
              >
                <p className="text-sm text-foreground/70">{facility}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {facilities.map((h, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void border border-gold-500/10 p-8 space-y-4"
              >
                <div className="text-gold-500">{h.icon}</div>
                <h3 className="font-heading text-xl">{h.title}</h3>
                <p className="text-foreground/60 text-sm leading-relaxed">{h.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Rooms */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto container-padding">
          <div className="text-center mb-14">
            <span className="overline">Where You'll Stay</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Rooms with <span className="text-gold-500">Resort Access</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {rooms.map((room, i) => (
              <motion.div
                key={room.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-void-subtle border border-gold-500/10 group"
              >
                <Link to={`/stay/${room.slug}`}>
                  <div className="p-6 space-y-3">
                    <span className="text-gold-500 text-xs uppercase tracking-widest">{room.category}</span>
                    <h3 className="font-heading text-xl group-hover:text-gold-500 transition-colors">{room.name}</h3>
                    <p className="text-foreground/60 text-sm leading-relaxed">{room.desc}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-gold-500/10">
                      <span className="text-gold-500 font-heading text-lg">₹{room.price.toLocaleString()}<span className="text-foreground/40 text-sm font-normal">/night</span></span>
                      <span className="text-foreground/40 text-sm">Up to {room.guests} guests</span>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/stay" className="btn-outline">View All Rooms</Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding bg-void-paper">
        <div className="max-w-3xl mx-auto container-padding">
          <div className="text-center mb-12">
            <span className="overline">Pool & Facilities</span>
            <h2 className="text-3xl md:text-4xl font-heading font-medium">
              Common <span className="text-gold-500">Questions</span>
            </h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, i) => (
              <div key={i} className="bg-void border border-gold-500/10">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className="font-heading text-lg pr-4">{faq.q}</span>
                  <ChevronDown
                    size={20}
                    className={`text-gold-500 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaq === i && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="px-6 pb-6"
                  >
                    <p className="text-foreground/70 leading-relaxed">{faq.a}</p>
                  </motion.div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-32">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url('/images/resort-pool-night.jpg')` }}
        />
        <div className="absolute inset-0 bg-void/85" />
        <div className="relative z-10 max-w-3xl mx-auto text-center px-6">
          <span className="overline">Reserve Your Stay</span>
          <h2 className="text-3xl md:text-5xl font-heading font-medium mb-6">
            The Pool is <span className="text-gold-500">Ready</span>
          </h2>
          <p className="text-foreground/70 mb-10 max-w-xl mx-auto">
            Book a room at Rudragarh and get full access to the pool, gym, dunes, and every one of our 18 on-site facilities — all included.
          </p>
          <Link to="/book" className="bg-gold-500 text-black px-8 py-4 uppercase tracking-widest text-sm hover:bg-gold-400 transition-colors inline-block">
            Book Your Stay
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ResortWithPool;
